import { TriangleAlertIcon, UsersIcon } from 'lucide-react';
import type { MembersManagementTableState } from '@/components/common/members-management';
import {
  MembersManagementPagination,
  MembersManagementPanel,
  MembersManagementTable,
  MembersManagementToolbar,
} from '@/components/common/members-management';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CompanyMemberRowActions } from './CompanyMemberRowActions';
import { CompanyMembersFilterSheet } from './CompanyMembersFilterSheet';
import {
  CompanyMembersColumnsControl,
  CompanyMembersSortControl,
} from './CompanyMembersViewControls';
import { getCompanyErrorMessage } from './company-errors';
import type { useCompanyMemberMutations } from './useCompanyMemberMutations';
import type { useCompanyMembersList } from './useCompanyMembersList';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

interface CompanyMembersDataPanelProps {
  filterSheetOpen: boolean;
  list: ReturnType<typeof useCompanyMembersList>;
  mutations: ReturnType<typeof useCompanyMemberMutations>;
  onFilterSheetOpenChange: (open: boolean) => void;
}

export function CompanyMembersDataPanel({
  filterSheetOpen,
  list,
  mutations,
  onFilterSheetOpenChange,
}: CompanyMembersDataPanelProps) {
  const rows = list.membersQuery.error ? [] : (list.membersQuery.data?.items ?? []);
  const tableState: MembersManagementTableState = list.membersQuery.isLoading
    ? { status: 'loading', showSkeleton: true }
    : list.membersQuery.error
      ? {
          status: 'error',
          content: (
            <Alert className="m-6" variant="destructive">
              <TriangleAlertIcon />
              <AlertTitle>Impossible de charger les membres</AlertTitle>
              <AlertDescription>
                {getCompanyErrorMessage(list.membersQuery.error)}
              </AlertDescription>
            </Alert>
          ),
        }
      : rows.length === 0
        ? {
            status: 'empty',
            content: (
              <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
                <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <UsersIcon className="size-6" />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-lg font-semibold text-foreground">
                    Aucun membre ne correspond à cette vue
                  </h3>
                  <p className="max-w-md text-sm leading-6 text-muted-foreground">
                    Ajustez la recherche, les filtres ou l’ordre d’affichage.
                  </p>
                </div>
              </div>
            ),
          }
        : { status: list.membersQuery.isFetching ? 'refreshing' : 'success' };
  const total = list.membersQuery.data?.meta.total ?? 0;
  const rangeStart = total === 0 ? 0 : (list.page - 1) * list.pageSize + 1;
  const rangeEnd = Math.min(list.page * list.pageSize, total);

  return (
    <MembersManagementPanel
      toolbar={(
        <MembersManagementToolbar
          activeFilters={list.activeFilters}
          columnsControl={(
            <CompanyMembersColumnsControl
              onToggle={list.toggleColumn}
              visible={list.visibleColumns}
            />
          )}
          filterControl={(
            <CompanyMembersFilterSheet
              onOpenChange={onFilterSheetOpenChange}
              onReset={list.resetFilters}
              onRoleChange={list.onRoleChange}
              onStatusChange={list.onStatusChange}
              onVerificationChange={list.onVerificationChange}
              open={filterSheetOpen}
              role={list.role}
              status={list.status}
              verification={list.verification}
            />
          )}
          onClearAllFilters={list.resetFilters}
          onSearchChange={list.onSearchChange}
          searchInputTestId="company-members-search"
          searchPlaceholder="Rechercher les membres"
          searchValue={list.search}
          sortControl={(
            <CompanyMembersSortControl
              direction={list.sortDirection}
              onDirectionChange={list.onDirectionChange}
              onSortChange={list.onSortChange}
              sortBy={list.sortBy}
            />
          )}
        />
      )}
      table={(
        <MembersManagementTable
          columns={list.columns}
          getRowKey={(member) => member.id}
          getRowTestId={(member) => `company-member-row-${member.id}`}
          renderActions={(member) => (
            <CompanyMemberRowActions
              isPending={mutations.isPending}
              member={member}
              onResendInvitation={mutations.resendInvitation}
              onRoleChange={mutations.changeRole}
              onStatusChange={mutations.changeStatus}
            />
          )}
          rows={rows}
          state={tableState}
        />
      )}
      pagination={(
        <MembersManagementPagination
          id="company-members-pagination"
          onPageChange={list.onPageChange}
          onPageSizeChange={list.onPageSizeChange}
          page={list.page}
          pageCount={list.membersQuery.data?.meta.pageCount ?? 1}
          pageSize={list.pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          totalLabel={`${rangeStart}–${rangeEnd} sur ${total} membre${total === 1 ? '' : 's'}`}
        />
      )}
    />
  );
}
